import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';

const Gallery = () => {
  const [selectedItem, setSelectedItem] = useState<number | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [imageLoadErrors, setImageLoadErrors] = useState<{ [key: number]: boolean }>({});
  const [hoveredItem, setHoveredItem] = useState<number | null>(null);

  const galleryItems = [
    {
      id: 1,
      type: 'image',
      src: '/gallery/gallery-1.jpg',
      description: 'Life before the ad industry'
    },
    {
      id: 2,
      type: 'image',
      src: '/gallery/gallery-2.jpg',
      description: 'Odesza at Red Rocks'
    },
    {
      id: 3,
      type: 'image',
      src: '/gallery/gallery-3.jpg',
      description: 'Covid Drive-In Rave'
    },
    {
      id: 4,
      type: 'image',
      src: '/gallery/gallery-4.jpg',
      description: 'Camp WhoShit'
    },
    {
      id: 5,
      type: 'image',
      src: '/gallery/gallery-5.jpg',
      description: 'Electric Forest'
    },
    {
      id: 6,
      type: 'image',
      src: '/gallery/gallery-6.jpg',
      description: 'Windsurfing on the Croix'
    },
    {
      id: 7,
      type: 'image',
      src: '/gallery/gallery-7.jpg',
      description: 'Berlin'
    },
    {
      id: 8,
      type: 'image',
      src: '/gallery/gallery-8.jpg',
      description: 'Favorite Film Portrait'
    },
    {
      id: 9,
      type: 'image',
      src: '/gallery/gallery-9.jpg',
      description: 'Me, Myself and Mamiya'
    },
    {
      id: 10,
      type: 'image',
      src: '/gallery/gallery-10.jpg',
      description: 'Iceland'
    },
  ];

  const handleImageError = (itemId: number) => {
    setImageLoadErrors(prev => ({ ...prev, [itemId]: true }));
  };

  const openLightbox = (index: number) => {
    setSelectedItem(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setSelectedItem(null);
  };

  const goToPrevious = () => {
    if (selectedItem !== null) {
      const newIndex = selectedItem === 0 ? galleryItems.length - 1 : selectedItem - 1;
      setSelectedItem(newIndex);
    }
  };

  const goToNext = () => {
    if (selectedItem !== null) {
      const newIndex = selectedItem === galleryItems.length - 1 ? 0 : selectedItem + 1;
      setSelectedItem(newIndex);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (lightboxOpen) {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          goToPrevious();
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          goToNext();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          closeLightbox();
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [lightboxOpen, selectedItem]);

  return (
    <div className="h-screen bg-transparent text-white overflow-hidden md:overflow-hidden overflow-y-auto">
      {/* Main Container */}
      <div className="h-full px-2 sm:px-4 lg:px-6 py-4 pb-24 md:pb-8">
        
        {/* Header */}
        <motion.div
          className="mb-6 lg:mb-8"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="relative max-w-fit">
            <div className="absolute top-2 left-2 w-full h-full bg-white/10"></div>
            <div className="relative bg-black border-4 border-white p-4 lg:p-6 transform -rotate-1">
              <h1 style={{ fontSize: 'clamp(1.5rem, 5vw, 3rem)' }} className="font-black uppercase leading-none">
                LIFE SNIPPETS
              </h1>
              <p className="text-white/60 font-mono text-xs uppercase">All shot on 120 film</p>
              <div className="w-12 lg:w-20 h-1.5 bg-white mt-2"></div>
            </div>
          </div>
        </motion.div>

        {/* Gallery Grid - Full Width Masonry Ready */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 lg:gap-4 w-full pb-24">
          {galleryItems.map((item, index) => (
            <motion.div
              key={item.id}
              className="group cursor-pointer"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              onClick={() => openLightbox(index)}
              onMouseEnter={() => setHoveredItem(index)}
              onMouseLeave={() => setHoveredItem(null)}
            >
              {/* Gallery Item */}
              <div className="relative h-48 sm:h-56 md:h-64 lg:h-72 xl:h-80">
                {/* Background shadow */}
                <div className="absolute top-2 left-2 w-full h-full bg-white"></div>
                
                {/* Main item */}
                <div className="relative bg-black border-3 border-white h-full overflow-hidden group-hover:transform group-hover:-translate-x-1 group-hover:-translate-y-1 transition-transform duration-200">
                  
                  {/* Media Container - Full Height */}
                  <div className="relative w-full h-full bg-gray-800 overflow-hidden">
                    {/* Try to load the actual image first */}
                    {!imageLoadErrors[item.id] ? (
                      <img
                        src={item.src}
                        alt="Gallery item"
                        className="w-full h-full object-cover"
                        onError={() => handleImageError(item.id)}
                      />
                    ) : (
                      /* Show placeholder when image fails to load */
                      <div className="w-full h-full bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center">
                        <div className="text-center">
                          <div className="w-12 h-12 bg-white/20 mx-auto mb-2 transform rotate-45"></div>
                          <p className="text-white/60 font-mono text-xs uppercase">IMAGE</p>
                        </div>
                      </div>
                    )}

                    {/* Desktop Hover Description */}
                    {hoveredItem === index && (
                      <motion.div
                        className="absolute inset-0 bg-black/80 flex items-center justify-center hidden md:flex"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.2 }}
                      >
                        <div className="bg-white text-black px-3 py-2 border-2 border-white transform -rotate-1">
                          <p className="font-black text-sm uppercase tracking-wide">
                            {item.description}
                          </p>
                        </div>
                      </motion.div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Lightbox Modal */}
      {lightboxOpen && selectedItem !== null && (
        <motion.div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={closeLightbox}
        >
          {/* Desktop Navigation Arrows - Outside Modal */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              goToPrevious();
            }}
            className="absolute left-8 top-1/2 transform -translate-y-1/2 text-white font-black text-4xl bg-white/10 border-2 border-white w-20 h-20 hidden md:flex items-center justify-center hover:bg-white hover:text-black transition-colors z-10"
          >
            ←
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              goToNext();
            }}
            className="absolute right-8 top-1/2 transform -translate-y-1/2 text-white font-black text-4xl bg-white/10 border-2 border-white w-20 h-20 hidden md:flex items-center justify-center hover:bg-white hover:text-black transition-colors z-10"
          >
            →
          </button>

          <motion.div
            className="relative max-w-6xl max-h-full"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.3 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={closeLightbox}
              className="absolute -top-12 right-0 text-white font-black text-xl bg-white/10 border-2 border-white px-4 py-2 hover:bg-white hover:text-black transition-colors"
            >
              CLOSE
            </button>

            {/* Image Counter */}
            <div className="absolute -top-12 left-0 text-white font-black text-lg">
              {selectedItem + 1} / {galleryItems.length}
            </div>
            
            {/* Lightbox Content */}
            <div className="bg-black border-4 border-white p-4 relative">
              {!imageLoadErrors[galleryItems[selectedItem].id] ? (
                <img
                  src={galleryItems[selectedItem].src}
                  alt="Image in lightbox"
                  className="max-w-full max-h-full object-contain mx-auto"
                />
              ) : (
                <div className="bg-gray-800 h-96 flex items-center justify-center">
                  <p className="text-white font-mono">IMAGE DISPLAY PLACEHOLDER</p>
                </div>
              )}

              {/* Lightbox Description - Bottom Right */}
              <div className="absolute bottom-4 right-4">
                <div className="bg-white text-black px-3 py-2 border-2 border-white transform rotate-1">
                  <p className="font-black text-sm uppercase tracking-wide">
                    {galleryItems[selectedItem].description}
                  </p>
                </div>
              </div>

              {/* Mobile Navigation - Bottom Center */}
              <div className="absolute bottom-[-80px] left-1/2 transform -translate-x-1/2 flex gap-4 md:hidden">
                <button
                  onClick={goToPrevious}
                  className="text-white font-black text-xl bg-white/10 border-2 border-white w-12 h-12 flex items-center justify-center hover:bg-white hover:text-black transition-colors"
                >
                  ←
                </button>
                <button
                  onClick={goToNext}
                  className="text-white font-black text-xl bg-white/10 border-2 border-white w-12 h-12 flex items-center justify-center hover:bg-white hover:text-black transition-colors"
                >
                  →
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default Gallery; 